import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, LogOut, Scissors, User as UserIcon,
  CheckCircle, TrendingUp, Users as UsersIcon,
  LogIn, LogOut as LogOutIcon, AlertTriangle, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';
import EmployeeProfileSettings from '../components/EmployeeProfileSettings';
import MonthPicker from '../components/MonthPicker';

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('planning');
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayBookings, setTodayBookings] = useState([]);
  const [stats, setStats] = useState({
    totalCoiffe: 0,
    nbClients: 0
  });

  const [period, setPeriod] = useState('day');
  const [statsLoading, setStatsLoading] = useState(false);

  // ── Pointage ──────────────────────────────────────────────────────────────
  const [todayPointage, setTodayPointage] = useState(null);
  const [pointageHistory, setPointageHistory] = useState([]);
  const [pointageLoading, setPointageLoading] = useState(false);
  const [clockLoading, setClockLoading] = useState(false);
  const [showDeclare, setShowDeclare] = useState(false);
  const [declaredTime, setDeclaredTime] = useState('');
  const [kiosqueCode, setKiosqueCode] = useState('');
  const [departNote, setDepartNote] = useState('');
  const [elapsed, setElapsed] = useState('');
  const [suspendedMsg, setSuspendedMsg] = useState('');

  // Chargement du profil une seule fois au montage
  useEffect(() => {
    api.get('/employee/me')
      .then(res => setEmployeeInfo(res.data.data))
      .catch(err => console.error("Erreur profil:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handler = (e) => setSuspendedMsg(e.detail?.message || 'Ce salon est suspendu. Veuillez contacter l\'administrateur de l\'outil pour le réactiver.');
    window.addEventListener('salon-suspended', handler);
    return () => window.removeEventListener('salon-suspended', handler);
  }, []);

  // Rechargement stats + RDV à chaque changement de période
  useEffect(() => {
    if (!employeeInfo) return;
    setStatsLoading(true);
    Promise.all([
      api.get(`/employee/my-stats?period=${period}`),
      api.get(`/employee/my-bookings?period=${period}`)
    ])
      .then(([statsRes, bookingsRes]) => {
        setStats({
          totalCoiffe: statsRes.data.data.totalGeneré,
          nbClients: statsRes.data.data.nbClients
        });
        setTodayBookings(bookingsRes.data.data);
      })
      .catch(err => console.error("Erreur stats:", err))
      .finally(() => setStatsLoading(false));
  }, [period, employeeInfo]);

  // ── Fonctions pointage ─────────────────────────────────────────────────────
  const [historyMonth, setHistoryMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const fetchPointageToday = useCallback(async () => {
    try {
      const res = await api.get('/pointage/me/today');
      setTodayPointage(res.data.data);
    } catch {}
  }, []);

  const fetchPointageHistory = useCallback(async () => {
    try {
      const res = await api.get(`/pointage/me?month=${historyMonth}`);
      setPointageHistory(res.data.data);
    } catch {}
  }, [historyMonth]);

  const loadPointage = useCallback(async () => {
    setPointageLoading(true);
    await Promise.all([fetchPointageToday(), fetchPointageHistory()]);
    setPointageLoading(false);
  }, [fetchPointageToday, fetchPointageHistory]);

  useEffect(() => {
    if (activeTab === 'pointage') loadPointage();
  }, [activeTab, loadPointage]);

  // Recharger l'historique quand le mois change
  useEffect(() => {
    if (activeTab === 'pointage') fetchPointageHistory();
  }, [historyMonth]); // eslint-disable-line

  // Compteur temps écoulé depuis arrivée
  useEffect(() => {
    if (!todayPointage?.arrivee || todayPointage.depart) { setElapsed(''); return; }
    const update = () => {
      const diffMin = Math.floor((Date.now() - new Date(todayPointage.arrivee)) / 60000);
      const h = Math.floor(diffMin / 60);
      const m = diffMin % 60;
      setElapsed(h > 0 ? `${h}h ${m}min` : `${m} min`);
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [todayPointage]);

  const handleClockIn = async () => {
    setClockLoading(true);
    try {
      const body = { code: kiosqueCode };
      if (showDeclare && declaredTime) {
        const today = new Date().toISOString().slice(0, 10);
        body.arriveeDeclaree = `${today}T${declaredTime}:00`;
      }
      await api.post('/pointage/arrivee', body);
      await fetchPointageToday();
      setShowDeclare(false);
      setDeclaredTime('');
      setKiosqueCode('');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du pointage');
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    setClockLoading(true);
    try {
      await api.post('/pointage/depart', { note: departNote });
      setDepartNote('');
      await fetchPointageToday();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du pointage');
    } finally {
      setClockLoading(false);
    }
  };

  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-';
  const fmtDate = (s) => s ? new Date(s + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : '-';
  const fmtDuree = (m) => {
    if (m === null || m === undefined) return '-';
    if (m < 60) return `${m} min`;
    return `${Math.floor(m / 60)}h ${m % 60 ? m % 60 + 'min' : ''}`.trim();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userName');
    navigate('/');
  };

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-slate-50">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
  const getCurrencyDisplay = () => {
  // On récupère la devise depuis les infos de l'employé (qui contient les infos du salon)
    const devise = employeeInfo?.salonId?.devise || 'XOF'; 
    
    if (devise === 'XOF') return 'FCFA';
    if (devise === 'EUR') return '€';
    if (devise === 'USD') return '$';
    return devise;
  };

return (
    <div className="min-h-screen bg-[#F8FAFC]">
      
      {/* SIDEBAR - Masquée sur Mobile, Visible sur Desktop */}
      <aside className="hidden md:flex w-72 bg-white border-r border-slate-200 p-8 flex-col fixed h-full z-20">
        <div className="flex items-center space-x-3 mb-10">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-100">
            <Scissors size={20}/>
          </div>
          <span className="text-xl font-black text-slate-800 tracking-tight">SamaSalon</span>
        </div>
        
        <nav className="flex-1 space-y-3">
          <button 
            onClick={() => setActiveTab('planning')}
            className={`w-full flex items-center space-x-3 p-4 rounded-2xl font-bold transition-all ${
              activeTab === 'planning' 
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' 
              : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
            }`}
          >
            <Calendar size={20}/> <span>Mon Planning</span>
          </button>

          <button
            onClick={() => setActiveTab('pointage')}
            className={`w-full flex items-center space-x-3 p-4 rounded-2xl font-bold transition-all ${
              activeTab === 'pointage'
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-100'
              : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
            }`}
          >
            <Clock size={20}/> <span>Pointage</span>
          </button>

          <button
            onClick={() => setActiveTab('profil')}
            className={`w-full flex items-center space-x-3 p-4 rounded-2xl font-bold transition-all ${
              activeTab === 'profil'
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-100'
              : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
            }`}
          >
            <UserIcon size={20}/> <span>Mon Profil</span>
          </button>
        </nav>

        <div className="mt-auto space-y-1">
          <button onClick={handleLogout} className="flex items-center space-x-3 text-red-500 font-bold p-4 w-full hover:bg-red-50 rounded-2xl transition-all">
            <LogOut size={20}/> <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* BARRE DE NAVIGATION BASSE - Uniquement sur Mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-around items-center z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
        <button 
          onClick={() => setActiveTab('planning')}
          className={`flex flex-col items-center p-2 ${activeTab === 'planning' ? 'text-blue-600' : 'text-slate-400'}`}
        >
          <Calendar size={24}/>
          <span className="text-[10px] font-bold mt-1 uppercase tracking-wider">Planning</span>
        </button>
        <button
          onClick={() => setActiveTab('pointage')}
          className={`flex flex-col items-center p-2 ${activeTab === 'pointage' ? 'text-blue-600' : 'text-slate-400'}`}
        >
          <Clock size={24}/>
          <span className="text-[10px] font-bold mt-1 uppercase tracking-wider">Pointage</span>
        </button>
        <button
          onClick={() => setActiveTab('profil')}
          className={`flex flex-col items-center p-2 ${activeTab === 'profil' ? 'text-blue-600' : 'text-slate-400'}`}
        >
          <UserIcon size={24}/>
          <span className="text-[10px] font-bold mt-1 uppercase tracking-wider">Profil</span>
        </button>
        <button onClick={handleLogout} className="flex flex-col items-center p-2 text-red-400">
          <LogOut size={24}/>
          <span className="text-[10px] font-bold mt-1 uppercase tracking-wider">Sortie</span>
        </button>
      </nav>

      {/* CONTENU PRINCIPAL */}
      <main className="flex-1 md:ml-72 p-4 md:p-12 pb-24 md:pb-12">

        {(employeeInfo?.salonId?.isActive === false || suspendedMsg) && (
          <div className="mb-6 rounded-2xl overflow-hidden shadow-lg border border-amber-200">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 to-red-400" />
            <div className="bg-amber-50 px-5 py-4 flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-black text-amber-900 text-sm tracking-tight">Ce salon est suspendu</p>
                <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
                  {suspendedMsg || 'Ce salon a été désactivé. Veuillez contacter l\'administrateur de l\'outil pour le réactiver.'}
                </p>
              </div>
              <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-[11px] font-black uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                Inactif
              </span>
            </div>
          </div>
        )}

        {activeTab === 'planning' && (
          <>
            <header className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-slate-900 leading-tight">Bonjour, {employeeInfo?.prenom}</h1>
                <p className="text-slate-500 font-medium text-sm mt-1">
                  Performances {{ day: 'du jour', week: 'de la semaine', month: 'du mois', year: "de l'année" }[period]}.
                </p>
              </div>
              <div className="bg-white px-5 py-4 rounded-[2rem] border border-slate-100 shadow-sm flex md:flex-col justify-between items-center md:items-end">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest md:mb-1">Total période</p>
                {statsLoading
                  ? <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mt-1" />
                  : <p className="text-xl font-black text-blue-600">{stats.totalCoiffe.toLocaleString()} {getCurrencyDisplay()}</p>
                }
              </div>
            </header>

            {/* BARRE DE FILTRE SCROLLABLE - Optimisée Mobile */}
            <div className="flex bg-slate-200/50 p-1 rounded-2xl w-full md:w-fit mb-8 border border-slate-100 overflow-x-auto no-scrollbar">
              <div className="flex min-w-full md:min-w-0">
                {[
                  { id: 'day', label: "Aujourd'hui" },
                  { id: 'week', label: "Semaine" },
                  { id: 'month', label: "Mois" },
                  { id: 'year', label: "Année" }
                ].map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPeriod(p.id)}
                    className={`flex-1 md:flex-none px-5 py-2.5 rounded-xl text-[11px] font-black transition-all whitespace-nowrap ${
                      period === p.id 
                      ? 'bg-white text-blue-600 shadow-md ring-1 ring-black/5' 
                      : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* STATS RAPIDES - 1 colonne sur mobile, 2 sur desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8">
              <div className="bg-slate-900 p-6 md:p-8 rounded-[2.5rem] text-white shadow-xl relative overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-white/10 rounded-xl text-blue-400"><TrendingUp size={20}/></div>
                    <span className="text-[10px] font-bold bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full uppercase">CA</span>
                  </div>
                  <p className="text-slate-400 text-sm font-medium">Chiffre d'affaires</p>
                  <h3 className="text-3xl md:text-4xl font-black mt-1">
                    {stats.totalCoiffe.toLocaleString()} 
                    <span className="text-lg font-normal text-slate-500 ml-2">{getCurrencyDisplay()}</span>
                  </h3>
                </div>
              </div>

              <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-slate-100 shadow-sm relative overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-xl"><UsersIcon size={20}/></div>
                  <span className="text-[10px] font-bold bg-slate-100 text-slate-400 px-3 py-1 rounded-full uppercase">Total</span>
                </div>
                <p className="text-slate-500 text-sm font-medium">Clients servis</p>
                <h3 className="text-3xl md:text-4xl font-black text-slate-900 mt-1">{stats.nbClients}</h3>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* PLANNING HEBDO */}
              <div className="bg-white p-6 md:p-8 rounded-[2.5rem] shadow-sm border border-slate-100 lg:col-span-1">
                <h3 className="font-bold text-slate-800 mb-6 flex items-center text-lg">
                  <Clock size={20} className="mr-3 text-blue-600"/> Mon Planning
                </h3>
                <div className="space-y-2">
                  {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((label, index) => {
                    const dayData = employeeInfo?.horaires?.find(h => h.jour === index);
                    const isToday = new Date().getDay() === index;
                    return (
                      <div key={label} className={`flex justify-between items-center p-4 rounded-2xl transition-all ${isToday ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-50 text-slate-500'}`}>
                        <span className="font-bold text-sm">{label}</span>
                        <span className={`text-xs font-bold ${isToday ? 'text-white' : 'text-slate-400'}`}>
                          {dayData ? `${dayData.debut} - ${dayData.fin}` : 'Repos'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* LISTE DES RDV */}
              <div className="bg-white p-6 md:p-8 rounded-[2.5rem] shadow-sm border border-slate-100 lg:col-span-2">
                <h3 className="font-bold text-slate-800 flex items-center text-lg mb-6">
                  <CheckCircle size={20} className="mr-3 text-emerald-500"/> Mes rendez-vous
                </h3>

                {todayBookings.length > 0 ? (
                  <div className="space-y-4">
                    {todayBookings.map((booking) => (
                      <div key={booking._id} className="flex items-center p-4 bg-slate-50 rounded-[2rem] border border-slate-100 transition-all active:scale-[0.98] group">
                        <div className="w-14 h-14 min-w-[3.5rem] bg-white rounded-2xl flex flex-col items-center justify-center text-blue-600 shadow-sm border border-slate-100">
                          {period !== 'day' && (
                            <span className="text-[9px] font-bold opacity-70 uppercase leading-none mb-1">
                              {new Date(booking.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                            </span>
                          )}
                          <span className="text-[11px] font-black leading-none">{booking.heureDebut}</span>
                        </div>
                        <div className="ml-4 flex-1 min-w-0">
                          <h4 className="font-black text-slate-800 truncate text-sm">{booking.clientNom}</h4>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">
                            {booking.prestation || 'Coiffure'}
                          </p>
                        </div>
                        <div className={`ml-2 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase whitespace-nowrap ${
                          booking.status === 'Confirmé' ? 'bg-blue-100 text-blue-600' : 
                          booking.status === 'Terminé' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-500'
                        }`}>
                          {booking.status}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                    <Calendar size={32} className="text-slate-300 mb-3"/>
                    <p className="text-slate-400 text-sm font-bold">Aucun rendez-vous</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── ONGLET POINTAGE ── */}
        {activeTab === 'pointage' && (
          <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl font-black text-slate-900">Mon Pointage</h1>
              <p className="text-slate-500 font-medium mt-1">Enregistrez vos heures de présence</p>
            </header>

            {pointageLoading ? (
              <div className="flex justify-center py-16">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-5">

                {/* Carte statut du jour */}
                <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Aujourd'hui</p>

                  {!todayPointage ? (
                    /* Pas encore pointé */
                    <div className="space-y-5">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
                          <Clock size={24} className="text-slate-400" />
                        </div>
                        <div>
                          <p className="font-black text-slate-800 text-lg">Non pointé</p>
                          <p className="text-slate-400 text-sm font-medium">Vous n'avez pas encore pointé l'arrivée</p>
                        </div>
                      </div>

                      {/* Code kiosque */}
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                          Code affiché sur la borne
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={kiosqueCode}
                          onChange={e => setKiosqueCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="_ _ _ _ _ _"
                          className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-black text-slate-700 text-2xl text-center tracking-[0.5em] focus:ring-2 ring-blue-400/20"
                        />
                      </div>

                      {/* Option arrivée déclarée */}
                      <button
                        onClick={() => setShowDeclare(!showDeclare)}
                        className="flex items-center gap-2 text-sm text-slate-400 font-bold hover:text-slate-600 transition-colors"
                      >
                        {showDeclare ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        J'étais là avant, déclarer une heure différente
                      </button>

                      {showDeclare && (
                        <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4">
                          <p className="text-xs font-bold text-orange-600 mb-3">
                            L'admin devra valider cette heure déclarée.
                          </p>
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                            Mon heure réelle d'arrivée
                          </label>
                          <input
                            type="time"
                            value={declaredTime}
                            onChange={e => setDeclaredTime(e.target.value)}
                            className="w-full p-3 bg-white border border-orange-200 rounded-xl outline-none font-black text-slate-700 text-lg focus:ring-2 ring-orange-300/30"
                          />
                        </div>
                      )}

                      <button
                        onClick={handleClockIn}
                        disabled={clockLoading || kiosqueCode.length !== 6 || (showDeclare && !declaredTime)}
                        className="w-full flex items-center justify-center gap-3 p-5 bg-emerald-500 text-white rounded-2xl font-black text-base shadow-lg shadow-emerald-100 hover:bg-emerald-600 transition-colors disabled:opacity-50"
                      >
                        {clockLoading
                          ? <><Loader2 size={20} className="animate-spin" /> Enregistrement…</>
                          : <><LogIn size={20} /> Pointer l'arrivée</>
                        }
                      </button>
                    </div>
                  ) : !todayPointage.depart ? (
                    /* En service */
                    <div className="space-y-5">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center relative">
                          <div className="w-3 h-3 rounded-full bg-emerald-500 absolute top-2 right-2 animate-pulse" />
                          <CheckCircle size={24} className="text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-black text-slate-800 text-lg">En service</p>
                          <p className="text-emerald-600 font-black text-2xl">{elapsed || '…'}</p>
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-2xl p-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400 font-medium">Arrivée enregistrée</span>
                          <span className="font-black text-slate-700">{fmtTime(todayPointage.arrivee)}</span>
                        </div>
                        {todayPointage.arriveeStatut === 'en_attente' && (
                          <div className="flex items-center gap-2 text-xs font-bold text-orange-500">
                            <AlertTriangle size={12} />
                            Heure déclarée ({fmtTime(todayPointage.arriveeDeclaree)}) - en attente de validation admin
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                          Note de fin de journée (optionnel)
                        </label>
                        <textarea
                          value={departNote}
                          onChange={e => setDepartNote(e.target.value)}
                          rows={3}
                          placeholder="Réalisations du jour, remarques, infos pour l'admin…"
                          className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-medium text-slate-700 text-sm resize-none focus:ring-2 ring-slate-300/50"
                        />
                      </div>

                      <button
                        onClick={handleClockOut}
                        disabled={clockLoading}
                        className="w-full flex items-center justify-center gap-3 p-5 bg-slate-800 text-white rounded-2xl font-black text-base hover:bg-black transition-colors disabled:opacity-50"
                      >
                        {clockLoading
                          ? <><Loader2 size={20} className="animate-spin" /> Enregistrement…</>
                          : <><LogOutIcon size={20} /> Pointer le départ</>
                        }
                      </button>
                    </div>
                  ) : (
                    /* Journée terminée */
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">
                          <CheckCircle size={24} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-black text-slate-800 text-lg">Journée terminée</p>
                          <p className="text-blue-600 font-black text-2xl">{fmtDuree(todayPointage.dureeMinutes)}</p>
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-2xl p-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400 font-medium">Arrivée</span>
                          <span className="font-black text-slate-700">{fmtTime(todayPointage.arrivee)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400 font-medium">Départ</span>
                          <span className="font-black text-slate-700">{fmtTime(todayPointage.depart)}</span>
                        </div>
                        {todayPointage.note && (
                          <div className="pt-2 border-t border-slate-200">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Note</p>
                            <p className="text-slate-600 font-medium leading-relaxed">{todayPointage.note}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Historique mensuel */}
                <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
                  {/* En-tête avec sélecteur de mois */}
                  <div className="p-6 border-b border-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <h3 className="font-black text-slate-800">Mon historique</h3>
                    <MonthPicker
                      value={historyMonth}
                      max={new Date().toISOString().slice(0, 7)}
                      onChange={setHistoryMonth}
                    />
                  </div>

                  {/* Total mensuel */}
                  {pointageHistory.length > 0 && (() => {
                    const totalMin = pointageHistory.reduce((acc, p) => acc + (p.dureeMinutes || 0), 0);
                    const h = Math.floor(totalMin / 60);
                    const m = totalMin % 60;
                    const total = h > 0 ? `${h}h ${m > 0 ? m + 'min' : ''}`.trim() : `${m} min`;
                    const jours = pointageHistory.filter(p => p.dureeMinutes != null).length;
                    return (
                      <div className="mx-6 my-4 p-4 bg-blue-50 rounded-2xl flex items-center justify-between">
                        <div>
                          <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Total du mois</p>
                          <p className="text-2xl font-black text-blue-700 mt-0.5">{total}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Jours travaillés</p>
                          <p className="text-2xl font-black text-blue-700 mt-0.5">{jours}</p>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="divide-y divide-slate-50">
                    {pointageHistory.length === 0 ? (
                      <p className="p-8 text-center text-slate-400 font-bold italic text-sm">Aucun pointage ce mois</p>
                    ) : (
                      pointageHistory.map(p => (
                        <div key={p._id} className="flex items-center justify-between px-6 py-4 gap-3">
                          <div>
                            <p className="text-sm font-bold text-slate-700">{fmtDate(p.date)}</p>
                            {p.arriveeStatut === 'en_attente' && (
                              <span className="text-[9px] font-black text-orange-500 uppercase flex items-center gap-1">
                                <AlertTriangle size={9} /> En attente de validation
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-slate-600">
                              {fmtTime(p.arrivee)} → {p.depart ? fmtTime(p.depart) : <span className="text-emerald-500">en cours</span>}
                            </p>
                            <p className="text-xs text-slate-400 font-medium">{fmtDuree(p.dureeMinutes)}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

              </div>
            )}
          </div>
        )}

        {/* ── ONGLET PROFIL ── */}
        {activeTab === 'profil' && (
          <div className="max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-500 p-2">
            <header className="mb-8">
              <h1 className="text-3xl font-black text-slate-900">Mon Profil</h1>
              <p className="text-slate-500 font-medium mt-1">Gérez vos informations personnelles</p>
            </header>
            <EmployeeProfileSettings userInitialData={employeeInfo} />
          </div>
        )}
      </main>
    </div>
  );
};

export default EmployeeDashboard;